import React, { Dispatch, SetStateAction, useState } from 'react';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    SyncOutlined
} from '@ant-design/icons';
import { Dropdown, MenuProps, Tag } from 'antd';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/button/Button';
import CustomTable from '../../../components/CustomTable';
import { Beneficiary } from '../../../interfaces/Beneficiary';
import { Transactions } from '../../../interfaces/Transactions';
import { formatCurrency, getTransactionStatus } from '../../../utils/helper';
import ApprovePaymentModal from './ApproveModal';
import FilterTransactions from './FilterTransactions';
import { ExportTransactions } from './ExportTransactions';
import {
    ExportTwoIcon,
    MoreIcon,
    RefreshCircleIcon
} from '../../../components/icons';
import { FilterParams, PagingParams } from '../../../interfaces/Filter';
import DashboardTableMobileCard from '../../../components/DashboardTableMobileCard';

interface ApprovalTableProps {
    isApprovalsLoading: boolean;
    approvalsList: Transactions[];
    fetchApprovals: () => void;
    isApprovalsFetching: boolean;
    beneficiaries: Beneficiary[];
    approvalsFilterOptions: FilterParams;
    setApprovalsFilterOptions: Dispatch<SetStateAction<FilterParams>>;
    approvalsPaging: PagingParams;
}

const ApprovalsTable = ({
    isApprovalsLoading,
    approvalsList,
    fetchApprovals,
    isApprovalsFetching,
    beneficiaries,
    approvalsFilterOptions,
    setApprovalsFilterOptions,
    approvalsPaging
}: ApprovalTableProps) => {
    const navigate = useNavigate();

    const [openModal, setOpenModal] = useState(false);
    const [openExport, setOpenExport] = useState(false);

    const [selected, setSelected] = useState<Transactions | null>(null);

    const getMenu = (record: {
        transactionReference: string;
    }): MenuProps['items'] => [
        {
            key: '1',
            label: (
                <div
                    className='flex items-center'
                    onClick={() => {
                        navigate(
                            `/payments/transactions/${record.transactionReference}`
                        );
                    }}
                >
                    <span className='ml-2'>View Details</span>
                </div>
            )
        }
    ];

    return (
        <div className='bg-white rounded-[8px] overflow-hidden'>
            <div className='flex justify-between items-center px-[24px] py-[20px]'>
                <h4 className='text-ebony font-semibold text-lg'>Approvals</h4>

                <div className='hidden lg:flex gap-2'>
                    <Button
                        leftIcon={<ExportTwoIcon />}
                        text='Export'
                        variant='secondaryOutline'
                        onClick={() => setOpenExport(true)}
                        size='sm'
                    />
                    <FilterTransactions
                        setTransactionFilterOptions={setApprovalsFilterOptions}
                        beneficiaries={beneficiaries}
                    />
                    <Button
                        leftIcon={<RefreshCircleIcon />}
                        text='Refresh'
                        variant='secondaryOutline'
                        onClick={fetchApprovals}
                        size='sm'
                    />
                </div>

                <div className='lg:hidden  relative'>
                    <Dropdown
                        menu={{
                            items: [
                                {
                                    key: '1',
                                    label: (
                                        <Button
                                            leftIcon={<RefreshCircleIcon />}
                                            text='Refresh'
                                            variant='tertiary'
                                            onClick={fetchApprovals}
                                        />
                                    )
                                },
                                {
                                    key: '2',
                                    label: (
                                        <FilterTransactions
                                            setTransactionFilterOptions={
                                                setApprovalsFilterOptions
                                            }
                                            beneficiaries={beneficiaries}
                                        />
                                    )
                                },
                                {
                                    key: '3',
                                    label: (
                                        <Button
                                            leftIcon={<ExportTwoIcon />}
                                            text='Export'
                                            variant='tertiary'
                                            onClick={() => setOpenExport(true)}
                                        />
                                    )
                                }
                            ]
                        }}
                        trigger={['click']}
                        dropdownRender={(menu) => (
                            <div className='w-[40vw]'>
                                {React.cloneElement(menu as React.ReactElement)}
                            </div>
                        )}
                    >
                        <button>
                            <MoreIcon />
                        </button>
                    </Dropdown>
                </div>
            </div>

            <ExportTransactions
                open={openExport}
                onClose={() => setOpenExport(false)}
                params={approvalsFilterOptions}
            />

            <CustomTable
                loadingData={isApprovalsLoading || isApprovalsFetching}
                data={approvalsList}
                columns={[
                    {
                        title: 'Status',
                        dataIndex: 'status',
                        key: 'status',
                        render: (_, { status }) => (
                            <Tag
                                bordered={false}
                                color={
                                    getTransactionStatus(status) ===
                                    'Processing'
                                        ? 'processing'
                                        : getTransactionStatus(status) ===
                                          'Completed'
                                        ? 'success'
                                        : getTransactionStatus(status) ===
                                          'Initiated'
                                        ? 'warning'
                                        : 'error'
                                }
                                icon={
                                    getTransactionStatus(status) ===
                                    'Processing' ? (
                                        <SyncOutlined spin />
                                    ) : getTransactionStatus(status) ===
                                      'Completed' ? (
                                        <CheckCircleOutlined />
                                    ) : getTransactionStatus(status) ===
                                      'Initiated' ? (
                                        <ExclamationCircleOutlined />
                                    ) : (
                                        <CloseCircleOutlined />
                                    )
                                }
                            >
                                {getTransactionStatus(status)}
                            </Tag>
                        )
                    },

                    {
                        title: 'Transaction ID',
                        dataIndex: 'transactionReference',
                        key: 'transactionReference',
                        render: (text, { transactionReference }) => (
                            <span
                                className='cursor-pointer text-appcolorprimary'
                                onClick={() => {
                                    navigate(
                                        `/payments/transactions/${transactionReference}`
                                    );
                                }}
                            >
                                {text}
                            </span>
                        )
                    },
                    {
                        title: 'From',
                        dataIndex: 'amountFrom',
                        key: 'amountFrom',
                        render: (text, { fromCurrencyCode }) => (
                            <span>
                                {formatCurrency(
                                    parseFloat(text),
                                    fromCurrencyCode
                                )}
                            </span>
                        )
                    },
                    {
                        title: 'To',
                        dataIndex: 'amountTo',
                        key: 'amountTo',
                        render: (text, { toCurrencyCode }) => (
                            <span>
                                {formatCurrency(
                                    parseFloat(text),
                                    toCurrencyCode
                                )}
                            </span>
                        )
                    },
                    {
                        title: 'Rate',
                        dataIndex: 'rateValue',
                        key: 'rateValue',
                        render: (text) => (
                            <span>
                                {parseFloat(text).toFixed(5).toLocaleString()}
                            </span>
                        )
                    },
                    {
                        title: 'Type',
                        dataIndex: 'transactionAction',
                        key: 'transactionAction'
                    },
                    {
                        title: 'Date',
                        dataIndex: 'createdDate',
                        key: 'createdDate',
                        render: (_, { createdDate }) => (
                            <span>
                                {dayjs(createdDate).format('DD MMM, YYYY')}
                            </span>
                        )
                    },
                    {
                        title: 'Action',
                        render: (_, record) => (
                            <Button
                                text='Approve/Reject'
                                onClick={() => {
                                    setSelected(record);
                                    setOpenModal(!openModal);
                                }}
                                variant='tertiary'
                            />
                        )
                    },
                    {
                        title: '',
                        render: (_, record) => (
                            <Dropdown
                                menu={{
                                    items: getMenu(record)
                                }}
                                trigger={['click']}
                            >
                                <div
                                    style={{
                                        cursor: 'pointer'
                                    }}
                                >
                                    <MoreIcon />
                                </div>
                            </Dropdown>
                        )
                    }
                ]}
                mobileComponent={<DashboardTableMobileCard />}
                onMobileRowClick={(data) =>
                    navigate(
                        `/payments/transactions/${data.transactionReference}`
                    )
                }
                showPagination
                paging={{
                    ...approvalsFilterOptions,
                    total: approvalsPaging.total
                }}
                updatePage={(value) => {
                    setApprovalsFilterOptions({
                        ...approvalsFilterOptions,
                        page: value
                    });
                }}
            />
            {selected && (
                <ApprovePaymentModal
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                    selected={selected}
                />
            )}
        </div>
    );
};

export default ApprovalsTable;
